import { type SxProps, type Theme, Box, TextField, Typography } from '@mui/material';
import { lengthRestrictions } from 'common/constants/length-restrictions';
import type { IPageFormV2 } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FaqBlock } from './faq-block';
import { styles } from './product-content-form.styles';
import { ProsBlock } from './pros-block';

interface IProductContentFormProps {
  productIndex: number;
}

export const ProductContentForm: FC<IProductContentFormProps> = ({ productIndex }) => {
  const {
    control,
    formState: { errors },
    register,
  } = useFormContext<IPageFormV2>();

  const prefix = `products.${productIndex}.content.` as const;

  const summaryValue = useWatch({ control, name: `${prefix}summary` }) || '';
  const bottomLineValue = useWatch({ control, name: `${prefix}bottomLine` }) || '';

  const LIMITS = {
    summary: lengthRestrictions.product.content.summary,
    bottomLine: 500,
  };

  const summaryError = errors?.products?.[productIndex]?.content?.summary;
  const bottomLineError = errors?.products?.[productIndex]?.content?.bottomLine;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.textAreasRow}>
        <Box sx={styles.textAreaWrapper}>
          <Typography mb={1} variant="subtitle2">
            Summary
          </Typography>
          <TextField
            {...register(`${prefix}summary`)}
            error={!!summaryError || summaryValue.length > LIMITS.summary}
            fullWidth
            helperText={summaryError?.message}
            multiline
            placeholder="Write your summary here..."
            rows={6}
            slotProps={{ htmlInput: { maxLength: LIMITS.summary } }}
            sx={
              [
                styles.multilineInput,
                { '& .MuiInputBase-root': { height: '140px' } },
              ] as SxProps<Theme>
            }
          />
          <Typography
            sx={
              [
                styles.charCount,
                { color: summaryValue.length > LIMITS.summary ? 'error.main' : 'text.secondary' },
              ] as SxProps<Theme>
            }
          >
            {summaryValue.length}/{LIMITS.summary}
          </Typography>
        </Box>

        <Box sx={styles.textAreaWrapper}>
          <Typography mb={1} variant="subtitle2">
            Bottom Line
          </Typography>
          <TextField
            {...register(`${prefix}bottomLine`)}
            error={!!bottomLineError || bottomLineValue.length > LIMITS.bottomLine}
            fullWidth
            helperText={bottomLineError?.message}
            multiline
            placeholder="Write your bottom line here..."
            rows={6}
            slotProps={{ htmlInput: { maxLength: LIMITS.bottomLine } }}
            sx={
              [
                styles.multilineInput,
                { '& .MuiInputBase-root': { height: '140px' } },
              ] as SxProps<Theme>
            }
          />
          <Typography
            sx={
              [
                styles.charCount,
                {
                  color:
                    bottomLineValue.length > LIMITS.bottomLine ? 'error.main' : 'text.secondary',
                },
              ] as SxProps<Theme>
            }
          >
            {bottomLineValue.length}/{LIMITS.bottomLine}
          </Typography>
        </Box>
      </Box>

      <ProsBlock productIndex={productIndex} />
      <FaqBlock productIndex={productIndex} />
    </Box>
  );
};
